<template>
  <div class="search">
    <el-input
      v-model="searchFilter.searchText"
      :placeholder="translate('Search') + ' ...'"
      @input="searchChanged"
      :style="highlightSearchText ? 'max-width:320px; border-radius: 4px; border: 2px solid var(--active-tab-color);' : 'max-width:320px'"
    />

    <div
      v-if="hasModes && initDefaultMode()"
      class="search-panel-item filter-modes"
    >
      <el-select
        v-model="searchFilter.mode"
        :collapse-tags="useCollapseTags"
        :collapse-tags-tooltip="useCollapseTags"
        @change="selectedModeChanged"
      >
        <el-option v-for="item in getModes" :key="item.id" :label="translate(item.text)" :value="item.id" >
        </el-option>
      </el-select>
    </div>

    <div
      v-if="hasOptions && initDefaultOptions()"
      class="search-panel-item filter-options"
    >
      <el-select
        v-model="searchFilter.options"
        multiple
        filterable
        clearable
        :collapse-tags="useCollapseTags"
        :collapse-tags-tooltip="useCollapseTags"
        @change="selectedOptionsChanged"
      >
        <el-option
          v-for="item in getOptions"
          :key="item.id"
          :label="item.text"
          :value="item.text"
        >
        </el-option>
      </el-select>
    </div>

    <div
      v-if="hasDateRange && initDefaultDateRange()"
      class="search-panel-item"
    >
      <el-date-picker
        :shortcuts="pickerShortcuts"
        :disabled-date="pickerOptions2 && pickerOptions2.disabledDate"
        :cell-class-name="pickerOptions2 && pickerOptions2.cellClassName"
        v-model="searchFilter.dateRange"
        type="daterange"
        
        unlink-panels
        range-separator="To"
        start-placeholder="From"
        end-placeholder="To"
        :format="getDateFormat"
        @change="selectedDateRangeChanged"
        
      >
      </el-date-picker>
    </div>
  </div>
</template>

<script>
import {  mapGetters } from 'vuex'

export default {
  name: 'CcSearch',

  props: {
    metadata: {},
    initialSearchText: undefined,
    useCollapseTags: false,
    highlightSearchText: false,
    /**
     * Settle time before the filter is emitted. This used to be `2200 * Math.random()`, which made
     * the delay unpredictable and occasionally near-zero - one request per keystroke on consumers
     * that now filter server-side.
     */
    debounceMs: {
      type: Number,
      default: 1500,
    },
  },

  data() {
    return {
      timeout: null,
      changes: {},
      searchFilter: { options: [], modes:[], dateRange: [], searchText: undefined },
      pickerOptions2: {
        defaultShortcuts: ["Last week","Last month","Last 3 months"],
        shortcuts: [
          {
            text: 'Today',
            textEn: 'Today',
            value: () => {
              const start = new Date()
              start.setHours(0, 0, 0, 0)
              const end = new Date()
              return [start, end]
            },
          },
          {
            text: 'Last week',
            textEn: 'Last week',
            value: () => {
              const end = new Date()
              const start = new Date()
              start.setDate(start.getDate() - 7)
              start.setHours(0, 0, 0, 0)
              return [start, end]
            },
          },
          {
            text: 'Last month',
            textEn: 'Last month',
            value: () => {
              const end = new Date()
              const start = new Date()
              start.setMonth(start.getMonth() - 1)
              start.setHours(0, 0, 0, 0)
              return [start, end]
            },
          },
          {
            text: 'Last 3 months',
            textEn: 'Last 3 months',
            value: () => {
              const end = new Date()
              const start = new Date()
              start.setMonth(start.getMonth() - 3)
              start.setHours(0, 0, 0, 0)
              return [start, end]
            },
          },
          {
            text: 'Last 6 months',
            textEn: 'Last 6 months',
            value: () => {
              const end = new Date()
              const start = new Date()
              start.setMonth(start.getMonth() - 6)
              start.setHours(0, 0, 0, 0)
              return [start, end]
            },
          },
          {
            text: 'Last year',
            textEn: 'Last year',
            value: () => {
              const start = new Date();
              start.setFullYear(start.getFullYear() - 1);
              start.setMonth(0, 1); // January 1st of last year
              start.setHours(0, 0, 0, 0);

              const end = new Date();
              end.setFullYear(end.getFullYear() - 1);
              end.setMonth(11, 31); // December 31st of last year
              end.setHours(23, 59, 59, 999);

              return [start, end];
            },
          },
          {
            text: 'Week to date',
            textEn: 'Week to date',
            value: () => {
              const end = new Date()
              const start = new Date()
              const day = start.getDay() || 7 // Make Sunday (0) count as 7
              start.setDate(start.getDate() - (day - 1))
              start.setHours(0, 0, 0, 0)
              return [start, end]
            },
          },
          {
            text: 'Month to date',
            textEn: 'Month to date',
            value: () => {
              const end = new Date()
              const start = new Date()
              start.setDate(1) // First day of the month
              start.setHours(0, 0, 0, 0)
              return [start, end]
            },
          },
        ],
      },
    }
  },

  mounted() {
    if(this.initialSearchText)
      this.searchFilter.searchText = this.initialSearchText
  },

  computed: {
    ...mapGetters(['globalMetadata']),

    pickerShortcuts() {
      var names = this.metadata && this.metadata.dateRangeShortcuts && this.metadata.dateRangeShortcuts.length > 0
        ? this.metadata.dateRangeShortcuts 
        : this.pickerOptions2.defaultShortcuts;

      var result = this.pickerOptions2.shortcuts.filter(n => names.includes(n.textEn));
      result.forEach(n => n.text = this.translate(n.textEn));
      
      return result;
    },

    hasModes() {
      return (
        this.metadata && this.metadata.modes && this.metadata.modes.length
      )
    },

    getModes() {
      return this.hasModes ? this.metadata.modes : []
    },

    hasOptions() {
      return (
        this.metadata && this.metadata.options && this.metadata.options.length
      )
    },

    getOptions() {
      return this.hasOptions ? this.metadata.options : []
    },

    hasDateRange() {
      return this.metadata && this.metadata.hasDateRange
    },

    getDefaultDateRange() {
      return this.hasDateRange && this.metadata.dateRange
        ? this.metadata.dateRange
        : null
    },

    getSearchText() {
      return this.searchFilter.searchText &&
        this.searchFilter.searchText.length >= 1
        ? this.searchFilter.searchText
        : undefined
    },

    getSelectedMode() {
      return this.hasModes ? this.searchFilter.mode : undefined
    },

    getFilterOptions() {
      return this.hasOptions ? this.searchFilter.options : []
    },
    
    getDateRange() {
      return this.hasDateRange ? this.searchFilter.dateRange : undefined
    },

    getDateFormat() {
      if (this.globalMetadata && this.globalMetadata.dateFormat) {
        return this.globalMetadata.dateFormat.toUpperCase()
      }

      return 'yyyy-MM-dd'.toUpperCase()
    },
  },

  methods: {
    searchChanged(text) {
      this.changes.searchText = true;
      this.onFilterChanged()
    },

    selectedModeChanged(val) {
      this.changes.mode = true;
      this.onFilterChanged()
    },

    selectedOptionsChanged(val) {
      this.changes.options = true;
      this.onFilterChanged()
    },

    selectedDateRangeChanged(val) {
      this.changes.dateRange = true;
      this.onFilterChanged()
    },

    onFilterChanged() {
      clearTimeout(this.timeout)

      this.timeout = setTimeout(() => {
        this.$emit('searchFilterChanged', {
          searchText: this.getSearchText,
          options: this.getFilterOptions,
          mode: this.getSelectedMode,
          dateRange: this.getDateRange,
          changes: JSON.parse(JSON.stringify(this.changes)),
        })

        this.changes = {};
      }, this.debounceMs)
    },

    initDefaultMode() {
      if (!this.searchFilter.defaultModeInitialized) {
        const modes = this.getModes;
        if(modes && modes.length) {
          var defaultMode = modes.find(n => n.isDefault);
          this.searchFilter.mode = defaultMode ? defaultMode.id : modes[0].id;
        }

        this.searchFilter.defaultModeInitialized = true;
      }

      return true;
    },

    initDefaultOptions() {
      if (!this.searchFilter.defaultOptionsInitialized) {
        this.getOptions.forEach((o) => {
          if (o.isDefault) {
            this.searchFilter.options.push(o.text)
          }
        })

        this.searchFilter.defaultOptionsInitialized = true
      }

      return true
    },

    initDefaultDateRange() {
      if (!this.searchFilter.defaultDateRangeInitialized) {
        if (this.getDefaultDateRange) {
          this.searchFilter.dateRange[0] = new Date(this.getDefaultDateRange[0])
          this.searchFilter.dateRange[1] = new Date(this.getDefaultDateRange[1])
        }

        this.searchFilter.defaultDateRangeInitialized = true
      }

      return true
    },

    reset() {
      this.searchFilter.searchText = ''
    }
  },
}
</script>

<style scoped>
.search {
  display: flex;
  margin-bottom: 4px;
}
.search-panel-item {
  margin-left: 8px;
}
.filter-options {
  min-width: 560px;
}
.filter-modes {
  min-width: 180px;
}
</style>
